import "maplibre-gl/dist/maplibre-gl.css";

import type { PickingInfo, ViewStateChangeParameters } from "@deck.gl/core";
import { MapController, MapView } from "@deck.gl/core";
import { GeoJsonLayer, ScatterplotLayer, SolidPolygonLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import type { FeatureCollection } from "geojson";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { GCP } from "~/features/georeferencer/utils";

interface GeoreferenceMapViewerProps {
  onMapClick?: (latitude: number, longitude: number) => void;
  gcps: GCP[];
  selectedGcpId?: string | null;
  onGcpSelect?: (gcpId: string) => void;
}

type ViewState = {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
};

export function GeoreferenceMapViewer({
  onMapClick,
  gcps,
  selectedGcpId = null,
  onGcpSelect,
}: GeoreferenceMapViewerProps) {
  const { t } = useTranslation();
  const [viewState, setViewState] = useState<ViewState>({
    longitude: 0,
    latitude: 0,
    zoom: 1,
    pitch: 0,
    bearing: 0,
  });

  const [worldBorders, setWorldBorders] = useState<FeatureCollection | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson")
      .then((response) => response.json())
      .then((data) => {
        setWorldBorders(data as FeatureCollection);
        setError(null);
      })
      .catch((_fetchError: unknown) => {
        setError(t("feature.georeferencer.errors.borderDataFetchFailed"));
      });
  }, [t]);

  const layers = [
    new SolidPolygonLayer({
      id: "ocean-background-layer",
      data: [
        {
          contour: [
            [-180, 90],
            [180, 90],
            [180, -90],
            [-180, -90],
          ],
        },
      ],
      getPolygon: (d) => d.contour,
      getFillColor: [255, 255, 255],
      filled: true,
      pickable: false,
    }),
    worldBorders &&
      new GeoJsonLayer({
        id: "land-layer",
        data: worldBorders,
        opacity: 1,
        filled: true,
        getFillColor: [230, 181, 108],
        pickable: false,
      }),
    worldBorders &&
      new GeoJsonLayer({
        id: "world-borders",
        data: worldBorders,
        opacity: 1,
        stroked: true,
        filled: false,
        lineWidthMinPixels: 1,
        getLineColor: [107, 68, 35],
        pickable: false,
      }),
    new ScatterplotLayer({
      id: "gcp-pins",
      data: gcps.map((gcp) => ({
        ...gcp,
        color: String(gcp.id) === String(selectedGcpId) ? [0, 255, 0] : [119, 119, 119],
      })),
      radiusScale: 6,
      radiusMinPixels: 5,
      radiusMaxPixels: 10,
      getPosition: (d) => [d.targetX, d.targetY],
      getFillColor: (d) => d.color,
      pickable: true,
      onClick: (info) => {
        const gcp = info.object as (GCP & { color: number[] }) | undefined;
        if (gcp && onGcpSelect) onGcpSelect(gcp.id);
        return true;
      },
    }),
  ].filter(Boolean);

  const handleClick = (info: PickingInfo) => {
    if (onMapClick && info.coordinate) onMapClick(info.coordinate[1], info.coordinate[0]);
  };

  const handleViewStateChange = (params: ViewStateChangeParameters) => {
    setViewState(params.viewState as ViewState);
  };

  return (
    <div className="flex h-full w-full flex-col rounded-[var(--radius-md)] border border-border-default bg-bg-surface p-3">
      <h2 className="mb-2 text-16-bold capitalize text-fg-1">{t("ui.map")}</h2>

      {error && (
        <div className="mb-2 rounded-[var(--radius-sm)] bg-danger-bg px-3 py-2 text-12-regular text-danger">
          {error}
        </div>
      )}

      <div className="relative h-full flex-grow overflow-hidden">
        <DeckGL
          initialViewState={viewState}
          onViewStateChange={handleViewStateChange}
          controller={{ type: MapController, keyboard: true, dragRotate: false, doubleClickZoom: false }}
          layers={layers}
          views={new MapView({ repeat: true })}
          onClick={handleClick}
          width="100%"
          height="100%"
          getCursor={() => "crosshair"}
        />
      </div>
    </div>
  );
}
